import { useState } from "react";
import { Star, WhatsappLogo, Check } from "@phosphor-icons/react";
import { api, fmtDate, waLink } from "@/lib/api";

const replyText = (c, place) => {
  const first = c.form.name.split(" ")[0];
  return c.feedback.rating >= 4
    ? `Hi ${first}, thank you so much for your ${c.feedback.rating}-star feedback on ${place}! It was a pleasure hosting you. You're welcome back anytime.`
    : `Hi ${first}, thank you for your honest feedback on ${place}. I'm sorry your stay wasn't perfect. I'd love to hear more so we can put it right.`;
};

function ReplyButton({ c, place }) {
  const [replied, setReplied] = useState(c.feedback.replied_at);
  const go = () => {
    api.post(`/checkins/${c.checkin_id}/replied`).then((r) => setReplied(r.data.replied_at)).catch(() => {});
  };
  return (
    <a href={waLink(c.form.phone, replyText(c, place))} target="_blank" rel="noreferrer" onClick={go} data-testid={`feedback-reply-${c.checkin_id}`}
      className={`mt-2 inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition-colors duration-200 ${replied ? "bg-secondary text-muted-foreground" : "bg-[#E3EEE2] text-[#3E6B3C] hover:bg-[#d3e6d1]"}`}>
      {replied ? <><Check weight="bold" /> Replied · reply again</> : <><WhatsappLogo weight="fill" /> {c.feedback.rating >= 4 ? "Thank on WhatsApp" : "Reply on WhatsApp"}</>}
    </a>
  );
}

export const avgOf = (list) => (list.length ? list.reduce((a, c) => a + c.feedback.rating, 0) / list.length : 0);

export const Stars = ({ value, size = 16 }) => (
  <span className="inline-flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={size} weight={n <= Math.round(value) ? "fill" : "regular"} className="text-[#D96C4A]" />)}</span>
);

function PropertyInsight({ p, list }) {
  const avg = avgOf(list);
  const dist = [5, 4, 3, 2, 1].map((n) => [n, list.filter((c) => c.feedback.rating === n).length]);
  const comments = list.slice(0, 3);
  return (
    <article className="rounded-2xl border border-border bg-white p-6" data-testid={`insight-${p.property_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0"><h3 className="truncate font-display text-lg">{p.data.name}</h3><p className="text-sm text-muted-foreground">{list.length} review{list.length === 1 ? "" : "s"}</p></div>
        <div className="text-right"><p className="font-display text-4xl leading-none" data-testid={`insight-avg-${p.property_id}`}>{list.length ? avg.toFixed(1) : "—"}</p><Stars value={avg} size={14} /></div>
      </div>
      {list.length > 0 ? (
        <>
          <div className="mt-5 space-y-1.5">
            {dist.map(([n, c]) => (
              <div key={n} className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="w-3">{n}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-[#D96C4A] transition-[width] duration-500" style={{ width: `${(c / list.length) * 100}%` }} /></div>
                <span className="w-5 text-right">{c}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-3 border-t border-border pt-4">
            {comments.length === 0 && <p className="text-sm text-muted-foreground">No written comments yet.</p>}
            {comments.map((c) => (
              <div key={c.checkin_id} data-testid={`insight-comment-${c.checkin_id}`}>
                <div className="flex items-center justify-between"><Stars value={c.feedback.rating} size={12} /><span className="text-xs text-muted-foreground">{fmtDate(c.feedback.created_at)}</span></div>
                {c.feedback.comment ? <p className="mt-1 text-sm">“{c.feedback.comment}”</p> : <p className="mt-1 text-sm text-muted-foreground">No comment</p>}
                <p className="text-xs text-muted-foreground">{c.form.name}</p>
                <ReplyButton c={c} place={p.data.name} />
              </div>
            ))}
          </div>
        </>
      ) : <p className="mt-5 text-sm text-muted-foreground">No feedback yet. Hand guests a receipt with the feedback QR code after check-in.</p>}
    </article>
  );
}

export function FeedbackInsights({ properties, feedback }) {
  if (!properties?.length) return null;
  return (
    <section className="mt-12" data-testid="feedback-insights">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="font-display text-xl">Feedback insights</h2>
        {feedback.length > 0 && <p className="text-sm text-muted-foreground" data-testid="insight-overall">Overall <b className="text-foreground">{avgOf(feedback).toFixed(1)}</b> from {feedback.length} review{feedback.length === 1 ? "" : "s"}</p>}
      </div>
      <div className="mt-4 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {properties.map((p) => <PropertyInsight key={p.property_id} p={p} list={feedback.filter((c) => c.property_id === p.property_id)} />)}
      </div>
    </section>
  );
}
